/**
 * ============================================================================
 * Auth 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Auth 模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createAuthApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createAuthApi } from '@bayes-mes/shared/api/auth/factory'
 * export const authApi = createAuthApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await authApi.login({ username: 'admin', password: '123456' })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createAuthApi } from '@bayes-mes/shared/api/auth/factory'
 * export const authApi = createAuthApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/auth/factory
 * @description Auth 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有基础认证方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  LoginForm,                    // 登录表单类型
  LogoutForm,                   // 登出表单类型
  UpdateProfileForm,            // 更新个人资料表单类型
  ChangePasswordForm,           // 修改密码表单类型
  LoginResponse,                // 登录响应类型
  RememberPasswordPolicySnapshot, // App 记住密码策略快照
  RefreshTokenResponse,         // 刷新Token响应类型
  RefreshAccessTokenResponse,  // 独立Refresh Token轮换响应类型
  PermissionsQueryParams,       // 权限查询参数类型
} from '../../types/auth'

// User 类型从 user 模块导入（auth 不再重复定义）
import type { User } from '../../types/user'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建 Auth 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有 Auth 相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 Auth 相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createAuthApi(client: IRequestClient) {
  return {
    // ===== 认证操作 =====

    /**
     * 获取 App 记住密码的最新运行时策略。
     *
     * 该接口无需登录，只返回全局开关和策略指纹，
     * 账号级是否允许保存由登录响应决定。
     */
    getRememberPasswordPolicy() {
      return client.get<RememberPasswordPolicySnapshot>(API_ROUTES.auth.rememberPasswordPolicy)
    },
    
    /**
     * 用户登录
     * 
     * 使用场景：
     * - 在登录页面，用户输入用户名和密码后点击"登录"按钮
     * - 支持用户名或邮箱登录
     * - 支持密码加密传输（前端加密，后端解密）
     * - 支持设备识别和绑定（每个设备独立Token）
     * 
     * @param data - 登录表单数据
     *   - username: string - 用户名或邮箱（必填）
     *     示例：'admin' 或 'admin@example.com'
     *   - password: string - 密码（必填，最小6位）
     *     示例：'123456'
     *   - device_name?: string - 设备名称（可选，最大100字符）
     *     示例：'Chrome浏览器'、'iPhone 14'
     *   - device_id?: string - 设备ID（可选，用于设备识别）
     *     示例：'web-chrome-xxxx' 或自动生成
     *   - encrypted?: boolean - 密码是否已加密（可选，默认false）
     *     true: 前端已加密，后端需要解密
     *     false: 明文传输（不推荐）
     * 
     * @returns Promise<LoginResponse> 登录成功的响应对象
     *   返回的数据结构：
     *   {
     *     user: User,                    // 用户详细信息
     *     token: string,                 // 访问令牌（Bearer Token）
     *     tokenType: 'Bearer',           // Token类型
     *     expiresIn: number,             // Token过期时间（秒）
     *     device: {                      // 当前设备信息
     *       deviceId: string,            // 设备ID
     *       deviceName: string,          // 设备名称
     *       deviceType: string,          // 设备类型（web/ios/android等）
     *       isTrusted: boolean,          // 是否受信任设备
     *       lastLoginAt: string          // 最后登录时间
     *     }
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在登录页面输入用户名和密码，点击"登录"按钮
     * 2. 前端验证：前端验证用户名和密码格式（非空、长度）
     * 3. 前端调用：authApi.login({ username: 'admin', password: '123456' })
     * 4. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 POST 请求
     *    - App端：uni.request 发送 POST 请求
     * 5. 网络传输：发送到后端 POST /api/v1/auth/login
     *    请求体：{ username: 'admin', password: '123456', ... }（JSON格式）
     * 6. 后端处理（AuthController@login 方法）：
     *    - 参数验证（username必填、password最小6位等）
     *    - 防暴力破解检查（IP锁定、用户名锁定）
     *    - 密码解密（如果encrypted=true）
     *    - 查找用户（支持用户名或邮箱）
     *    - 验证密码（Hash::check）
     *    - 检查用户状态（是否禁用）
     *    - 创建或更新设备记录
     *    - 生成Token（绑定设备）
     *    - 记录登录日志
     *    - 初始化Redis会话
     * 7. 响应返回：后端返回 JSON 格式的登录响应
     * 8. 适配器解包：适配器提取 response.data.data（实际数据）
     * 9. 前端接收：组件收到 LoginResponse 类型的数据
     * 10. 前端处理：
     *     - 保存Token到本地存储（localStorage/cookie）
     *     - 保存用户信息到状态管理（Vuex/Pinia）
     *     - 跳转到首页或目标页面
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：基础登录
     * const result = await authApi.login({
     *   username: 'admin',
     *   password: '123456'
     * })
     * console.log(result.user.name)  // 用户名
     * console.log(result.token)      // Token
     * 
     * // 示例2：带设备信息的登录
     * const result2 = await authApi.login({
     *   username: 'admin@example.com',
     *   password: '123456',
     *   device_name: 'Chrome浏览器',
     *   device_id: 'web-chrome-12345'
     * })
     * 
     * // 示例3：在登录组件中使用
     * const handleLogin = async () => {
     *   try {
     *     const result = await authApi.login({
     *       username: formData.username,
     *       password: formData.password
     *     })
     *     
     *     // 保存Token
     *     localStorage.setItem('token', result.token)
     *     
     *     // 保存用户信息
     *     userStore.setUser(result.user)
     *     
     *     // 跳转到首页
     *     router.push('/')
     *     
     *     message.success('登录成功')
     *   } catch (error) {
     *     message.error('登录失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 401：用户名或密码错误
     * - 403：账号已被禁用
     * - 422：参数验证失败（用户名为空、密码太短等）
     * - 429：登录失败次数过多，账号或IP被锁定
     * - 500：服务器内部错误
     * 
     * 安全特性：
     * - 登录失败限制（5次失败锁定20分钟）
     * - Token绑定设备（每设备独立Token，防止Token被盗用）
     * - 密码加密传输（前端加密，后端解密，可选）
     * - 设备信息记录（记录浏览器、IP等信息）
     * - 审计日志记录（记录每次登录尝试）
     */
    login(data: LoginForm) {
      // POST 请求，第二个参数是请求体数据
      // 后端会验证数据并返回登录响应
      return client.post<LoginResponse>(API_ROUTES.auth.login, data)
    },
    
    /**
     * 用户登出
     * 
     * 使用场景：
     * - 用户点击页面右上角的"退出登录"按钮
     * - 支持登出当前设备
     * - 支持登出所有设备（强制下线）
     * 
     * @param data - 登出表单数据（可选）
     *   - all_devices?: boolean - 是否登出所有设备（可选）
     *     true: 删除所有设备的Token，用户在所有设备上都会被登出
     *     false/undefined: 只登出当前设备
     * 
     * @returns Promise<void> 登出成功不返回数据
     * 
     * 数据流向：
     * 1. 用户点击"退出登录"按钮
     * 2. 前端调用：authApi.logout() 或 authApi.logout({ all_devices: true })
     * 3. 发送请求：POST /api/v1/auth/logout
     *    请求头：Authorization: Bearer <token>（需要认证）
     *    请求体：{ all_devices: true/false }（可选）
     * 4. 后端处理（AuthController@logout 方法）：
     *    - 验证Token（通过 auth:sanctum 中间件）
     *    - 获取设备ID（从请求头 X-Device-ID）
     *    - 如果 all_devices=true：
     *      * 删除用户的所有Token
     *      * 清除所有设备记录的Token ID
     *      * 清理所有Redis会话
     *    - 如果 all_devices=false（默认）：
     *      * 删除当前Token
     *      * 清除当前设备的Token ID
     *      * 清理当前设备的Redis会话
     *    - 记录审计日志
     * 5. 响应返回：返回成功消息
     * 6. 前端处理：
     *    - 清除本地存储的Token
     *    - 清除用户信息
     *    - 跳转到登录页
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：登出当前设备
     * await authApi.logout()
     * 
     * // 示例2：登出所有设备
     * await authApi.logout({ all_devices: true })
     * 
     * // 示例3：在组件中使用
     * const handleLogout = async () => {
     *   try {
     *     await authApi.logout()
     *     
     *     // 清除本地数据
     *     localStorage.removeItem('token')
     *     userStore.clearUser()
     *     
     *     // 跳转到登录页
     *     router.push('/login')
     *     
     *     message.success('已退出登录')
     *   } catch (error) {
     *     message.error('退出失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 401：Token无效或已过期（未登录）
     * - 500：服务器内部错误
     */
    logout(data?: LogoutForm) {
      // POST 请求，登出操作
      // data 可选，用于指定是否登出所有设备
      return client.post<void>(API_ROUTES.auth.logout, data)
    },
    
    /**
     * 获取当前用户信息
     * 
     * 使用场景：
     * - 页面刷新后，根据Token重新获取用户信息
     * - 用户信息变更后，刷新当前用户信息
     * - 在用户中心页面显示用户详细资料
     * 
     * @returns Promise<User> 当前登录用户的详细信息
     *   返回的数据结构：
     *   {
     *     id: number,              // 用户ID
     *     username: string,        // 用户名
     *     name: string,            // 姓名
     *     nickname?: string,       // 昵称
     *     email?: string,          // 邮箱
     *     phone?: string,          // 手机号
     *     avatar?: string,         // 头像URL
     *     status: UserStatus,      // 用户状态（0-禁用，1-启用）
     *     lastLoginAt?: string,    // 最后登录时间
     *     lastLoginIp?: string,    // 最后登录IP
     *     createdAt: string,       // 创建时间
     *     updatedAt: string,       // 更新时间
     *     roles?: string[],        // 角色ID列表
     *     roleNames?: string[]     // 角色名称列表
     *   }
     * 
     * 数据流向：
     * 1. 用户操作：页面刷新或需要显示用户信息
     * 2. 前端调用：authApi.me()
     * 3. 发送请求：GET /api/v1/auth/me
     *    请求头：Authorization: Bearer <token>（需要认证）
     * 4. 后端处理（AuthController@me 方法）：
     *    - 验证Token（通过 auth:sanctum 中间件）
     *    - 获取当前登录用户
     *    - 检查用户状态（是否禁用）
     *    - 预加载角色关系
     *    - 通过 UserResource 格式化数据
     * 5. 响应返回：返回用户详细信息
     * 6. 前端处理：
     *    - 更新状态管理中的用户信息
     *    - 在页面上显示用户信息
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取用户信息
     * const user = await authApi.me()
     * console.log(user.name)      // 姓名
     * console.log(user.roleNames) // 角色列表
     * 
     * // 示例2：页面初始化时获取用户信息
     * onMounted(async () => {
     *   try {
     *     const user = await authApi.me()
     *     userStore.setUser(user)
     *   } catch (error) {
     *     // Token无效，跳转到登录页
     *     router.push('/login')
     *   }
     * })
     * 
     * // 示例3：刷新用户信息
     * const refreshUserInfo = async () => {
     *   const user = await authApi.me()
     *   userStore.setUser(user)
     *   message.success('用户信息已更新')
     * }
     * ```
     * 
     * 错误处理：
     * - 401：Token无效或已过期（未登录）
     * - 403：账号已被禁用
     * - 500：服务器内部错误
     */
    me() {
      // GET 请求，获取当前用户信息
      // 需要在请求头中携带有效的Token
      return client.get<User>(API_ROUTES.auth.me)
    },

    /**
     * 更新当前用户个人资料
     *
     * 使用场景：
     * - App/Web 个人中心修改自己的昵称、手机号、邮箱、头像
     * - 不需要系统用户管理权限，后端通过 Token 定位当前用户
     * - 不允许更新用户名、姓名、状态、角色、密码等系统管理字段
     *
     * @param data - 个人资料表单数据
     * @returns Promise<User> 更新后的当前用户信息
     */
    updateProfile(data: UpdateProfileForm) {
      return client.put<User>(API_ROUTES.auth.updateProfile, data)
    },
    
    /**
     * 刷新 Token
     * 
     * 使用场景：
     * - Token即将过期时，自动刷新延长有效期
     * - 长时间操作的应用，定期刷新Token保持会话
     * 
     * @returns Promise<RefreshTokenResponse> 新的Token信息
     *   返回的数据结构：
     *   {
     *     token: string,         // 新的访问令牌
     *     tokenType: 'Bearer',   // Token类型
     *     expiresIn: number      // 过期时间（秒）
     *   }
     * 
     * 数据流向：
     * 1. 前端检测：Token即将过期（通常在过期前5-10分钟刷新）
     * 2. 前端调用：authApi.refreshToken()
     * 3. 发送请求：POST /api/v1/auth/refresh
     *    请求头：Authorization: Bearer <旧token>（需要认证）
     * 4. 后端处理（AuthController@refresh 方法）：
     *    - 验证旧Token（通过 auth:sanctum 中间件）
     *    - 获取当前用户
     *    - 检查用户状态（是否禁用）
     *    - 获取当前Token信息（设备名称等）
     *    - 删除旧Token
     *    - 生成新Token（继承原Token的设备名称）
     *    - 更新设备记录的Token ID
     *    - 记录日志
     * 5. 响应返回：返回新的Token信息
     * 6. 前端处理：
     *    - 更新本地存储的Token
     *    - 更新请求头中的Token
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：手动刷新Token
     * const result = await authApi.refreshToken()
     * localStorage.setItem('token', result.token)
     * 
     * // 示例2：自动刷新Token（定时器）
     * const setupTokenRefresh = () => {
     *   // 每25分钟刷新一次（假设Token有效期30分钟）
     *   setInterval(async () => {
     *     try {
     *       const result = await authApi.refreshToken()
     *       localStorage.setItem('token', result.token)
     *       console.log('Token已刷新')
     *     } catch (error) {
     *       console.error('Token刷新失败', error)
     *       // 跳转到登录页
     *       router.push('/login')
     *     }
     *   }, 25 * 60 * 1000)
     * }
     * 
     * // 示例3：在响应拦截器中自动刷新
     * axios.interceptors.response.use(
     *   response => response,
     *   async error => {
     *     if (error.response.status === 401) {
     *       // Token过期，尝试刷新
     *       try {
     *         const result = await authApi.refreshToken()
     *         localStorage.setItem('token', result.token)
     *         
     *         // 重新发送原请求
     *         error.config.headers.Authorization = `Bearer ${result.token}`
     *         return axios.request(error.config)
     *       } catch (refreshError) {
     *         // 刷新失败，跳转到登录页
     *         router.push('/login')
     *         return Promise.reject(refreshError)
     *       }
     *     }
     *     return Promise.reject(error)
     *   }
     * )
     * ```
     * 
     * 错误处理：
     * - 401：Token无效或已过期（无法刷新，需要重新登录）
     * - 403：账号已被禁用
     * - 500：服务器内部错误
     * 
     * 注意事项：
     * - 刷新Token会删除旧Token，旧Token立即失效
     * - 同一时间只能有一个有效Token（绑定设备）
     * - 建议在Token过期前5-10分钟刷新，避免在请求中途过期
     */
    refreshToken() {
      // POST 请求，刷新Token
      // 会删除旧Token并生成新Token
      return client.post<RefreshTokenResponse>(API_ROUTES.auth.refresh)
    },

    /**
     * 使用独立 Refresh Token 轮换会话（行业标准双 Token 刷新通道）
     *
     * 与 refreshToken() 的区别：本方法携带长效独立凭证（Refresh Token），
     * access token 已过期后仍可换取新会话；refreshToken() 要求携带仍有效的
     * access token，过期后即无法刷新。
     *
     * 安全语义（后端 RefreshTokenService）：
     * - Refresh Token 一次性轮换：响应返回新的 refreshToken，旧凭证即刻作废
     * - 已轮换凭证在宽限窗（默认60秒）内重复提交 → 401 + reason=refresh_rotated_grace，
     *   调用方应改用持久层最新凭证重试（跨标签页并发刷新竞态）
     * - 超过宽限窗的重复提交视为泄露 → 401 + reason=refresh_reused，整个轮换家族被撤销
     * - 闲置超过后端 session_timeout 对应平台窗口 → 401 + reason=refresh_idle_timeout，
     *   须重新登录（与"30分钟无操作强制下线"语义一致）
     * - 请求头 X-Device-ID 必须与签发时一致，否则 401 + reason=refresh_device_mismatch
     *
     * @param data - refresh_token：登录/上一次轮换返回的独立刷新凭证
     * @returns Promise<RefreshAccessTokenResponse> 新 access token + 新 refresh token
     */
    refreshAccessToken(data: { refresh_token: string }) {
      return client.post<RefreshAccessTokenResponse>(
        API_ROUTES.auth.refreshToken,
        data,
      )
    },

    /**
     * 修改密码
     * 
     * 使用场景：
     * - 用户在个人中心修改密码
     * - 用户定期更新密码
     * - 密码泄露后及时修改
     * 
     * @param data - 修改密码表单数据
     *   - old_password: string - 旧密码（必填，最小6位）
     *   - new_password: string - 新密码（必填，最小6位，不能与旧密码相同）
     *   - new_password_confirmation: string - 确认新密码（必填，必须与新密码一致）
     * 
     * @returns Promise<void> 修改成功不返回数据
     * 
     * 数据流向：
     * 1. 用户在修改密码页面输入旧密码和新密码
     * 2. 前端验证：新密码长度、两次密码一致性
     * 3. 前端调用：authApi.changePassword({ ... })
     * 4. 发送请求：POST /api/v1/auth/change-password
     *    请求头：Authorization: Bearer <token>（需要认证）
     *    请求体：{ old_password, new_password, new_password_confirmation }
     * 5. 后端处理（AuthController@changePassword 方法）：
     *    - 验证Token（通过 auth:sanctum 中间件）
     *    - 验证旧密码是否正确
     *    - 验证新密码格式和一致性
     *    - 更新密码（Hash加密）
     *    - 删除所有Token（强制重新登录）
     *    - 清除所有设备记录
     *    - 清理Redis会话
     *    - 记录审计日志
     * 6. 响应返回：返回成功消息
     * 7. 前端处理：
     *    - 提示用户密码修改成功
     *    - 清除本地Token和用户信息
     *    - 跳转到登录页
     * 
     * 代码示例：
     * ```typescript
     * // 示例：修改密码
     * try {
     *   await authApi.changePassword({
     *     old_password: '123456',
     *     new_password: 'newPassword123',
     *     new_password_confirmation: 'newPassword123'
     *   })
     *   
     *   // 清除本地数据
     *   localStorage.removeItem('token')
     *   userStore.clearUser()
     *   
     *   // 跳转到登录页
     *   router.push('/login')
     *   
     *   message.success('密码修改成功，请重新登录')
     * } catch (error) {
     *   message.error('修改密码失败：' + error.message)
     * }
     * ```
     * 
     * 错误处理：
     * - 401：旧密码错误
     * - 422：参数验证失败（新密码太短、两次密码不一致等）
     * - 500：服务器内部错误
     * 
     * 安全特性：
     * - 必须验证旧密码
     * - 新密码必须与旧密码不同
     * - 修改后强制所有设备重新登录
     * - 记录审计日志
     */
    changePassword(data: ChangePasswordForm) {
      // POST 请求，修改密码
      // 修改成功后会删除所有Token，用户需要重新登录
      return client.post<void>(API_ROUTES.auth.changePassword, data)
    },

    /**
     * 获取当前用户的权限列表
     * 
     * 使用场景：
     * - 用户登录后获取权限码列表
     * - 用于前端权限控制（按钮、功能等）
     * - 支持 Web 和 App 平台分别获取对应权限
     * 
     * @param params - 查询参数（可选）
     *   - platform?: 'web' | 'app' - 平台类型，默认 'web'
     * 
     * @returns Promise<string[]> 权限码数组
     *   返回的数据结构：
     *   [
     *     "sales:order:create",      // 销售订单创建权限
     *     "sales:order:edit",        // 销售订单编辑权限
     *     "material:view",           // 物料查看权限
     *     "warehouse:manage"         // 仓库管理权限
     *   ]
     * 
     * 数据流向：
     * 1. 用户登录成功后
     * 2. 前端调用：authApi.getPermissions({ platform: 'app' })
     * 3. 发送请求：GET /api/v1/auth/permissions?platform=app
     *    Header: Authorization: Bearer {token}
     * 4. 后端处理：
     *    - 从 Token 解析出当前用户
     *    - 查询用户的角色和权限
     *    - 根据平台参数过滤权限
     *    - 返回权限码列表
     * 5. 前端处理：
     *    - 保存权限列表到 Store
     *    - 用于权限检查（hasPermission）
     * 
     * 代码示例：
     * ```typescript
     * // Web 端获取权限
     * const permissions = await authApi.getPermissions({ platform: 'web' })
     * accessStore.setAccessCodes(permissions)
     * 
     * // App 端获取权限
     * const permissions = await authApi.getPermissions({ platform: 'app' })
     * userStore.permissions = permissions
     * 
     * // 使用默认平台（web）
     * const permissions = await authApi.getPermissions()
     * ```
     * 
     * 平台过滤规则：
     * - 权限表的 platform 字段为空：返回（通用权限）
     * - 权限表的 platform 字段为 'all'：返回（全平台权限）
     * - 权限表的 platform 字段匹配请求平台：返回
     * - 其他情况：不返回
     * 
     * 错误处理：
     * - 401：未登录或 Token 过期
     * - 403：账号已被禁用
     * - 422：平台参数无效
     * 
     * 安全特性：
     * - 通过 Token 认证，无需传递用户ID
     * - 后端根据用户角色动态计算权限
     * - 超级管理员获取所有权限
     */
    getPermissions(params?: PermissionsQueryParams) {
      // GET 请求，获取当前用户的权限码列表
      // 支持平台参数，默认为 web
      return client.get<string[]>(API_ROUTES.auth.permissions, { params })
    },
  }
}
